import { createAction, props } from "@ngrx/store";
import { BaseBankAccount } from "../../interfaces";
import { BankAccountErrorData } from "../../interfaces/bank-account-form";

/**
 * Action to fetch bank information data based on a routing number.
 * 
 * @param routingNumber - The routing number used to fetch bank information.
 */
export const fetchBankInfoData = createAction(
    `[Bank Account Form] fetch Bank Info Data`,
    props<{ routingNumber: string }>()
);

/**
 * Action dispatched when fetching bank information data is successful.
 * 
 * @param routingNumber - The routing number used in the request.
 * @param bankName - The name of the bank associated with the routing number.
 */
export const fetchBankInfoDataSuccess = createAction(
    `[Bank Account Form] fetch Bank Info Data Success`,
    props<{ routingNumber: string, bankName: string }>()
);

/**
 * Action dispatched when there is an error fetching bank information data.
 * 
 * @param bankAccountErrorData - The error data related to the failed fetch operation.
 */
export const fetchBankInfoDataError = createAction(
    `[Bank Account Form] fetch Bank Info Data Error`,
    props<{ bankAccountErrorData: BankAccountErrorData }>()
);

/**
 * Action to reset the fetch bank information data error state.
 * 
 * This action is dispatched to clear any existing errors related to fetching bank information.
 * It is typically used when the user modifies the routing number or resets the form.
 */
export const resetFetchBankInfoDataError = createAction(
    `[Bank Account Form] reset Fetch Bank Info Data Error`
);

/**
 * Action to update bank account information.
 * 
 * @param bankData - The updated bank account data.
 */
export const updateBankInformationData = createAction(
    `[Bank Account Form] update Bank Information Data`,
    props<{ bankData: BaseBankAccount }>()
);

/**
 * Action dispatched when updating bank account information is successful.
 */
export const updateBankInformationDataSuccess = createAction(
    `[Bank Account Form] update Bank Information Data Success`
);

/**
 * Action dispatched when there is an error updating bank account information.
 * 
 * @param error - The error message describing the failure.
 */
export const updateBankInformationDataError = createAction(
    `[Bank Account Form] update Bank Information Data Error`,
    props<{ error: string }>()
);

/**
 * Action to reset the state of the bank information form.
 */
export const resetSubmittingBankInformationForm = createAction(
    `[Bank Account Form] reset Bank Information Form`
);